import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../redux/store";
import { activityPing, getDeviceIdentifier } from "../services/authService";

/** How often to send an activity ping (ms). */
const PING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Sends a periodic activity ping while the user is authenticated.
 * Also pings when the tab regains visibility (e.g. after alt-tab).
 *
 * Usage: mount once near the app root, e.g. inside `<AppLayout>`.
 */
export function useActivityPing() {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const ping = () => {
      activityPing(getDeviceIdentifier());
    };

    // Ping on mount / when auth changes
    ping();

    // Periodic ping
    intervalRef.current = setInterval(ping, PING_INTERVAL_MS);

    // Ping on visibility change (tab becomes active)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ping();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated]);
}
